import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Collapse, Select, Space, Spin } from "antd";
import {
  getDayAHeadPricesOptions,
  getLifecycleAroundActiveTransactionsOptions,
  getMeasurementsForSiteQuarterHourlyOptions,
  getOptimizationResultOptions,
  getSiteOptions,
  listProfilesOptions,
  optimizationsGetTaskOptions,
} from "../../services/client/@tanstack/react-query.gen";
import { JsonEditor } from "json-edit-react";
import { Card } from "antd";
import Title from "antd/es/typography/Title";
import {
  getAllAssetIds,
  parseDayAHeadPricesToTimeSeries,
  parseScheduleWithMeasurementsToCumulativeEnergyTimeSeries,
  parseScheduleWithMeasurementsToTimeSeries,
} from "../../components/SiteDetails/Components/dataUtils";
import { TimeSeriesChart } from "../../components/SiteDetails/Charts/TimeSeriesChart";
import { useMemo } from "react";
import { TransactionsTable } from "../../components/SiteDetails/TransactionsTable";
import ErrorMessage from "../../components/util-components/ErrorMessage";
import NoData from "../../components/NewSite/Components/OptimizationConfigurationForm/NoData";
import { ONE_SECOND, parseIsoDurationToMs } from "../../util/time";
import { tableFromIPC } from "@uwdata/flechette";
import {
  GetProfileDto,
  Measurement,
  TransactionReadable,
} from "../../services/client";
import { WarningsAlert } from "./OptimizationWarnings";
import { isDynamic } from "../../util/contractUtils";
import { isViewAsAdmin } from "../../util/viewAsAdmin";
import { formatDateTime, formatTime } from "../../util/formatDate";

const { Panel } = Collapse;

const MAX_CHARGERS_FOR_ENERGY_CHART = 5;

const resolveEffectiveInputs = (
  event: TransactionReadable,
  profile: GetProfileDto | undefined,
): { minEnergyWh: number | null; departure: Date | null } => {
  const override = profile?.override_transaction_input === true;

  const rawMinEnergyWh =
    "requestedMinEnergy" in event ? event.requestedMinEnergy : null;
  const useDefaultMin =
    override || rawMinEnergyWh == null || rawMinEnergyWh <= 0;
  const minEnergyWh = useDefaultMin
    ? (profile?.ac?.default_requested_min_energy ?? rawMinEnergyWh ?? null)
    : rawMinEnergyWh;

  const rawDeparture = event.estimatedDepartureTime;
  const useDefaultDeparture = override || rawDeparture == null;
  let departure: Date | null = null;
  if (
    useDefaultDeparture &&
    profile?.ac?.default_estimated_departure_time_delta &&
    event.startTime
  ) {
    const deltaMs = parseIsoDurationToMs(
      profile.ac.default_estimated_departure_time_delta,
    );
    departure = new Date(new Date(event.startTime).getTime() + deltaMs);
  } else if (rawDeparture) {
    departure = new Date(rawDeparture);
  }

  return { minEnergyWh, departure };
};

export const OptimizationTaskDetails = () => {
  const { id } = useParams();
  const [emptySchedule, setEmptySchedule] = useState(false);
  const [selectedChargerIds, setSelectedChargerIds] = useState<string[]>([]);

  const { isLoading, error, data } = useQuery({
    ...optimizationsGetTaskOptions({ path: { id: id! } }),
    enabled: !!id,
  });

  const { data: siteData } = useQuery({
    ...getSiteOptions({
      path: { site_id: data?.parameters?.site_id as string },
    }),
    enabled: !!data?.parameters?.site_id,
  });

  const {
    isLoading: optimizationLoading,
    error: optimizationError,
    data: optimizationData,
  } = useQuery({
    ...getOptimizationResultOptions({
      path: { id: data?.response?.optimization_id as string },
    }),
    enabled:
      !!data && data.status === "DONE" && !!data.response?.optimization_id,
  });

  const site_id = data && (data?.parameters?.["site_id"] as string);
  const warnings =
    (data?.response?.warnings as {
      type: string;
      message: string;
      transaction_id?: string;
    }[]) || [];

  const optimizationTime = optimizationData?.timestamp;
  const midnight = useMemo(
    () =>
      optimizationTime
        ? new Date(new Date(optimizationTime).setHours(0, 0, 0, 0))
        : new Date(),
    [optimizationTime],
  );

  const firstScheduleTime = useMemo(() => {
    if (!optimizationData?.schedules) {
      return null;
    }

    if (optimizationData.schedules.length === 0) {
      setEmptySchedule(true);
      return null;
    }

    const firstSchedule = optimizationData.schedules[0];

    return new Date(firstSchedule?.profile[0].timestamp);
  }, [optimizationData]);

  const contract = siteData?.configurations?.contract;
  const usesDayAheadPrices = isDynamic(contract) || contract == null;
  const { isLoading: dayAHeadPricesLoading, data: dayAHeadPrices } = useQuery({
    ...getDayAHeadPricesOptions({
      query: {
        bidding_zone: isDynamic(siteData?.configurations?.contract)
          ? siteData.configurations.contract.day_ahead_bidding_zone
          : "BE",
        at_time: midnight.toISOString(),
      },
    }),
    enabled:
      usesDayAheadPrices &&
      siteData?.configurations?.optimization_configuration
        ?.cost_minimization_active,
  });

  const allAssetIds = useMemo(() => {
    if (!siteData) {
      return [];
    }
    return getAllAssetIds(siteData);
  }, [siteData]);

  const { data: profilesPage } = useQuery({
    ...listProfilesOptions({
      path: { site_id: site_id! },
      query: { page_size: 100 },
    }),
    enabled: !!site_id,
  });
  const profileById = useMemo(() => {
    const map = new Map<string, GetProfileDto>();
    for (const p of profilesPage?.data ?? []) {
      map.set(p.profile_id, p);
    }
    return map;
  }, [profilesPage]);

  const { data: unparsedMeasurementData } = useQuery({
    ...getMeasurementsForSiteQuarterHourlyOptions({
      path: {
        site_id: site_id!, // This will always be defined because of the enabled condition
      },
      query: {
        start: midnight.toISOString(),
        // Don't include the measurement for the first schedule time as we will have that in the schedule itself
        stop: new Date(
          new Date(firstScheduleTime!).getTime() - ONE_SECOND,
        ).toISOString(),
      },
      body: allAssetIds,
      parseAs: "arrayBuffer",
    }),
    enabled:
      site_id != null &&
      optimizationTime != null &&
      allAssetIds &&
      allAssetIds.length > 0,
  });
  const measurementData: Measurement[] | null = useMemo(() => {
    if (unparsedMeasurementData == null) {
      return null;
    }
    return tableFromIPC(unparsedMeasurementData as ArrayBuffer, {
      useProxy: false,
    }).toArray() as Measurement[];
  }, [unparsedMeasurementData]);

  const {
    data: transactions,
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useQuery({
    ...getLifecycleAroundActiveTransactionsOptions({
      query: { before: optimizationTime! },
      path: {
        site_id: site_id!,
      },
    }),
    enabled: !!site_id && !!optimizationTime,
  });
  const parsedData = useMemo(
    () =>
      optimizationData
        ? parseScheduleWithMeasurementsToTimeSeries(
            optimizationData,
            measurementData ?? [],
          )
        : null,
    [optimizationData, measurementData],
  );

  const cumulativeEnergyData = useMemo(
    () =>
      optimizationData
        ? parseScheduleWithMeasurementsToCumulativeEnergyTimeSeries(
            optimizationData,
            measurementData ?? [],
          )
        : null,
    [optimizationData, measurementData],
  );

  const filterByCharger = <T extends { name: string }>(series: T[]): T[] =>
    selectedChargerIds.length === 0
      ? series
      : series.filter((s) => selectedChargerIds.includes(s.name));

  const filteredPowerData = parsedData ? filterByCharger(parsedData) : null;
  const filteredPowerDataInKW = filteredPowerData?.map((series) => ({
    ...series,
    values: series.values.map((v) => (v == null ? null : v / 1000)),
  }));
  const filteredEnergyData = cumulativeEnergyData
    ? filterByCharger(cumulativeEnergyData)
    : null;

  const filteredTransactions = useMemo(() => {
    if (!transactions) {
      return undefined;
    }
    if (selectedChargerIds.length === 0) {
      return transactions;
    }
    return transactions.filter((t) => selectedChargerIds.includes(t.assetId));
  }, [transactions, selectedChargerIds]);

  const singleChargerEffectiveValues = useMemo(() => {
    if (selectedChargerIds.length !== 1) {
      return null;
    }
    if (!optimizationTime) {
      return null;
    }
    const list = filteredTransactions ?? [];
    if (list.length === 0) {
      return null;
    }

    const optTimeMs = new Date(optimizationTime).getTime();
    const eventTime = (t: TransactionReadable) =>
      new Date(t.received_at ?? t.timestamp ?? t.startTime).getTime();

    const eventsByTxId = new Map<string, TransactionReadable[]>();
    for (const event of list) {
      const existing = eventsByTxId.get(event.transactionId);
      if (existing) {
        existing.push(event);
      } else {
        eventsByTxId.set(event.transactionId, [event]);
      }
    }

    const minEnergiesWh = new Set<number>();
    const departureMs = new Set<number>();
    for (const events of eventsByTxId.values()) {
      const snapshotAtOptTime = events
        .filter((e) => eventTime(e) <= optTimeMs)
        .reduce<TransactionReadable | null>(
          (latest, e) =>
            latest == null || eventTime(e) > eventTime(latest) ? e : latest,
          null,
        );
      if (!snapshotAtOptTime) {
        continue;
      }
      if (snapshotAtOptTime.transactionState === "Ended") {
        continue;
      }

      const { minEnergyWh, departure } = resolveEffectiveInputs(
        snapshotAtOptTime,
        snapshotAtOptTime.profile_id
          ? profileById.get(snapshotAtOptTime.profile_id)
          : undefined,
      );
      if (minEnergyWh != null && minEnergyWh > 0) {
        minEnergiesWh.add(minEnergyWh);
      }
      if (departure != null) {
        departureMs.add(departure.getTime());
      }
    }
    return {
      minEnergiesWh: [...minEnergiesWh].sort((a, b) => a - b),
      departureMs: [...departureMs].sort((a, b) => a - b),
    };
  }, [filteredTransactions, selectedChargerIds, profileById, optimizationTime]);

  const effectiveChargerCount =
    selectedChargerIds.length === 0
      ? (filteredEnergyData?.length ?? 0)
      : selectedChargerIds.length;
  const showEnergyChart =
    effectiveChargerCount > 0 &&
    effectiveChargerCount < MAX_CHARGERS_FOR_ENERGY_CHART;

  if (error || optimizationError) {
    return (
      <ErrorMessage
        message={
          error?.message ||
          optimizationError?.message ||
          "An error occurred while fetching the data."
        }
      />
    );
  }

  const viewAsAdmin = isViewAsAdmin();

  if (isLoading || optimizationLoading) {
    return <Spin />;
  }

  if (
    !data ||
    !site_id ||
    !id ||
    !firstScheduleTime ||
    !optimizationTime ||
    emptySchedule
  ) {
    return (
      <NoData
        title="No transactions active"
        message="There are no transactions active for this optimization task, so no schedules were generated."
      />
    );
  }

  return (
    <Card
      title={`Optimization Task - ${data?.parameters?.site_id} - ${formatDateTime(optimizationTime)}`}
      bordered={false}
      loading={isLoading}
    >
      {viewAsAdmin && warnings && warnings.length > 0 && (
        <WarningsAlert warnings={warnings} />
      )}
      <div className="flex justify-around gap-4">
        <div className="flex-grow">
          {parsedData && (
            <div>
              <Space className="mb-4" size="middle">
                <span>Chargers:</span>
                <Select
                  mode="multiple"
                  allowClear
                  value={selectedChargerIds}
                  onChange={setSelectedChargerIds}
                  placeholder="All chargers"
                  style={{ minWidth: 320 }}
                  options={allAssetIds.map((id) => ({
                    value: id,
                    label: id,
                  }))}
                />
              </Space>

              <Title level={3}>Charging Power</Title>

              <TimeSeriesChart
                data={filteredPowerDataInKW ?? []}
                title="Optimization Output - Charging Power"
                className="w-full"
                isLoading={optimizationLoading}
                yAxisTitle="Power"
                yAxisTickSuffix=" kW"
                extra={{
                  verticalLines: firstScheduleTime
                    ? [
                        {
                          timestamp: new Date(firstScheduleTime),
                          label: `⏱︎ Start schedules: ${formatTime(firstScheduleTime)}`,
                          color: "#1e8c6f",
                        },
                      ]
                    : [],
                }}
              />

              {showEnergyChart && (
                <>
                  <Title level={3}>Cumulative energy charged</Title>

                  <TimeSeriesChart
                    data={filteredEnergyData ?? []}
                    title="Cumulative Energy Charged"
                    className="w-full"
                    isLoading={optimizationLoading}
                    line="smooth"
                    yAxisTitle="Energy"
                    yAxisTickSuffix=" kWh"
                    extra={{
                      verticalLines: [
                        ...(firstScheduleTime
                          ? [
                              {
                                timestamp: new Date(firstScheduleTime),
                                label: `⏱︎ Start schedules: ${formatTime(firstScheduleTime)}`,
                                color: "#1e8c6f",
                              },
                            ]
                          : []),
                        ...(
                          singleChargerEffectiveValues?.departureMs ?? []
                        ).map((ms) => ({
                          timestamp: new Date(ms),
                          label: `🚗 Departure: ${formatTime(ms)}`,
                          color: "#d97706",
                        })),
                      ],
                      horizontalLines: (
                        singleChargerEffectiveValues?.minEnergiesWh ?? []
                      ).map((wh) => ({
                        value: wh / 1000,
                        label: `Min requested: ${(wh / 1000).toLocaleString()} kWh`,
                        color: "#d97706",
                      })),
                    }}
                  />
                </>
              )}

              <Title level={3}>Transactions</Title>

              {filteredTransactions && (
                <TransactionsTable
                  transactions={filteredTransactions}
                  loading={transactionsLoading}
                  error={!!transactionsError}
                />
              )}
            </div>
          )}
          {!!siteData?.configurations?.optimization_configuration
            ?.cost_minimization_active &&
            dayAHeadPrices && (
              <div>
                <Title level={3}>Day a head prices</Title>
                <TimeSeriesChart
                  data={[parseDayAHeadPricesToTimeSeries(dayAHeadPrices)]}
                  title="Optimization Input - Day a Head Prices"
                  className="w-full"
                  isLoading={dayAHeadPricesLoading}
                />
              </div>
            )}

          {viewAsAdmin && (
            <>
              <Title level={3}>Debug information</Title>

              <Collapse>
                <Panel header="Input" key="1">
                  <div className="flex gap-4 justify-between">
                    <JsonEditor data={data} collapse={1} minWidth={500} />
                  </div>
                </Panel>
                {optimizationData && (
                  <Panel header="Output" key="2">
                    <div className="flex gap-4 justify-between">
                      <JsonEditor
                        data={optimizationData}
                        collapse={1}
                        minWidth={800}
                      />
                    </div>
                  </Panel>
                )}
              </Collapse>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
